import { useState, useRef, useEffect, KeyboardEvent, useCallback } from "react";
import { useCalculatorStore, HistoryEntry } from "../store/calculatorStore";
import { PlotViewer } from "./PlotViewer";
import { MatrixViewer } from "./MatrixViewer";
import type { CalcArray, CalcMatrix } from "@quanta/calcscript";
import {
  getAutocompleteSuggestions,
  getDoc,
  searchDocs,
  getAllDocs,
  FunctionDoc,
  ConstantDoc,
  CommandDoc,
} from "../data/builtinDocs";

interface AutocompleteItem {
  name: string;
  type: "function" | "constant" | "command" | "variable";
  synopsis?: string;
}

// Format a man page for display
function formatManPage(doc: FunctionDoc | ConstantDoc | CommandDoc): string {
  const lines: string[] = [];

  if ("synopsis" in doc) {
    lines.push(`NAME`);
    lines.push(`       ${doc.name}`);
    lines.push("");
    lines.push(`SYNOPSIS`);
    lines.push(`       ${doc.synopsis}`);
    lines.push("");
    lines.push(`DESCRIPTION`);
    lines.push(`       ${doc.description}`);

    if ("parameters" in doc && doc.parameters && doc.parameters.length > 0) {
      lines.push("");
      lines.push(`PARAMETERS`);
      for (const param of doc.parameters) {
        const opt = param.optional ? " (optional)" : "";
        const def = param.default ? ` [default: ${param.default}]` : "";
        lines.push(`       ${param.name} : ${param.type}${opt}${def}`);
        lines.push(`           ${param.description}`);
      }
    }

    if ("returns" in doc && doc.returns) {
      lines.push("");
      lines.push(`RETURNS`);
      lines.push(`       ${doc.returns}`);
    }

    if ("options" in doc && doc.options && doc.options.length > 0) {
      lines.push("");
      lines.push(`OPTIONS`);
      for (const opt of doc.options) {
        lines.push(`       ${opt.flag}`);
        lines.push(`           ${opt.description}`);
      }
    }

    if ("examples" in doc && doc.examples && doc.examples.length > 0) {
      lines.push("");
      lines.push(`EXAMPLES`);
      for (const ex of doc.examples) {
        lines.push(`       ${ex.code}`);
        if ("result" in ex && ex.result)
          lines.push(`           => ${ex.result}`);
        if ("description" in ex && ex.description)
          lines.push(`           ${ex.description}`);
        lines.push("");
      }
    }

    if ("seeAlso" in doc && doc.seeAlso && doc.seeAlso.length > 0) {
      lines.push(`SEE ALSO`);
      lines.push(`       ${doc.seeAlso.join(", ")}`);
    }

    if ("category" in doc) {
      lines.push("");
      lines.push(`CATEGORY`);
      lines.push(`       ${doc.category}`);
    }
  } else {
    // Constant
    lines.push(`NAME`);
    lines.push(`       ${doc.name}`);
    lines.push("");
    lines.push(`VALUE`);
    lines.push(`       ${doc.value}`);
    lines.push("");
    lines.push(`DESCRIPTION`);
    lines.push(`       ${doc.description}`);
  }

  return lines.join("\n");
}

// Process shell commands (man, clear, help, etc.)
function processShellCommand(
  input: string,
  clearHistory: () => void,
  getEnvVars: () => string[],
  getHistory: () => HistoryEntry[],
): {
  handled: boolean;
  output?: string;
  isManPage?: boolean;
  isList?: boolean;
} {
  const trimmed = input.trim();
  const parts = trimmed.split(/\s+/);
  const cmd = parts[0].toLowerCase();

  if (cmd === "clear") {
    clearHistory();
    return { handled: true };
  }

  if (cmd === "history") {
    const historyEntries = getHistory();
    const limit = parts[1] ? parseInt(parts[1], 10) : historyEntries.length;
    const entries = historyEntries.slice(-Math.max(1, limit));

    if (entries.length === 0) {
      return { handled: true, output: "No command history." };
    }

    const lines = entries.map((entry, idx) => {
      const num = (historyEntries.length - entries.length + idx + 1)
        .toString()
        .padStart(4);
      return `${num}  ${entry.input}`;
    });

    return { handled: true, output: lines.join("\n"), isList: true };
  }

  if (cmd === "man" || cmd === "help") {
    if (parts.length === 1 && cmd === "help") {
      // General help
      return {
        handled: true,
        output:
          `Quanta Calculator Shell\n` +
          `━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
          `Type mathematical expressions to evaluate them.\n` +
          `Use Tab for autocomplete.\n\n` +
          `SHELL COMMANDS:\n` +
          `  man <topic>    Show manual page for a function or constant\n` +
          `  man -k <word>  Search manual pages\n` +
          `  man -l         List all available topics\n` +
          `  clear          Clear terminal output\n` +
          `  env            Show defined variables\n` +
          `  reset          Reset environment\n` +
          `  history [n]    Show command history\n\n` +
          `EXAMPLES:\n` +
          `  2 + 2                    Basic arithmetic\n` +
          `  sin(pi/4)                Trigonometry\n` +
          `  [1,2;3,4] * [5;6]        Matrix multiplication\n` +
          `  x = linspace(0, 2*pi, 100); plot(x, sin(x))\n\n` +
          `Type 'man -l' to see all available functions.`,
        isManPage: true,
      };
    }

    if (parts[1] === "-k" && parts[2]) {
      // Search
      const results = searchDocs(parts[2]);
      if (results.length === 0) {
        return { handled: true, output: `No results found for "${parts[2]}"` };
      }
      const output = results
        .map((r) => `  ${r.name.padEnd(15)} ${r.description.slice(0, 60)}...`)
        .join("\n");
      return {
        handled: true,
        output: `Search results for "${parts[2]}":\n\n${output}`,
        isList: true,
      };
    }

    if (parts[1] === "-l") {
      // List all
      const allDocs = getAllDocs();
      const byCategory: Record<string, string[]> = {};

      for (const doc of allDocs) {
        const cat =
          doc.category || (doc.type === "constant" ? "Constants" : "Commands");
        if (!byCategory[cat]) byCategory[cat] = [];
        byCategory[cat].push(doc.name);
      }

      const lines: string[] = ["Available Manual Pages:", ""];
      for (const [cat, names] of Object.entries(byCategory).sort()) {
        lines.push(`${cat}:`);
        lines.push(`  ${names.join(", ")}`);
        lines.push("");
      }
      return { handled: true, output: lines.join("\n"), isList: true };
    }

    if (parts[1]) {
      // Specific topic
      const doc = getDoc(parts[1]);
      if (doc) {
        return { handled: true, output: formatManPage(doc), isManPage: true };
      }
      return { handled: true, output: `No manual entry for "${parts[1]}"` };
    }

    return {
      handled: true,
      output: `Usage: man <topic> | man -k <keyword> | man -l`,
    };
  }

  if (cmd === "env") {
    const vars = getEnvVars();
    if (vars.length === 0) {
      return { handled: true, output: "No variables defined." };
    }
    return {
      handled: true,
      output: `Defined variables:\n  ${vars.join("\n  ")}`,
      isList: true,
    };
  }

  if (cmd === "reset") {
    return { handled: false }; // Let the store handle this
  }

  return { handled: false };
}

interface TerminalLineProps {
  entry: HistoryEntry;
  onViewMatrix: (entry: HistoryEntry) => void;
  onDelete: () => void;
  onEdit: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

function TerminalLine({
  entry,
  onViewMatrix,
  onDelete,
  onEdit,
  isCollapsed,
  onToggleCollapse,
}: TerminalLineProps) {
  const resultType =
    entry.result && typeof entry.result === "object" && "type" in entry.result
      ? (entry.result as { type: string }).type
      : null;
  // A multi-statement block carries ordered text/plot segments; render those
  // interleaved. A single plot expression carries it as the result value.
  const hasSegments =
    Array.isArray(entry.segments) && entry.segments.length > 0;
  const hasPlots =
    !hasSegments && Array.isArray(entry.plots) && entry.plots.length > 0;
  const isPlot =
    entry.type === "plot" &&
    resultType === "plot" &&
    !hasPlots &&
    !hasSegments;
  const isMatrix =
    entry.type === "matrix" ||
    entry.type === "array" ||
    resultType === "matrix";
  const isManPage = entry.type === "man-page";
  const isList = entry.type === "list";

  return (
    <div className="terminal-entry">
      {/* Input line with prompt */}
      <div className="terminal-input-line">
        <span className="terminal-prompt">&gt;&gt;</span>
        <code
          className="terminal-input-text"
          onClick={onEdit}
          title="Click to edit"
        >
          {entry.input}
        </code>
        <button
          className="terminal-delete-btn"
          onClick={onDelete}
          title="Delete"
        >
          ×
        </button>
      </div>

      {/* Output */}
      {entry.isError ? (
        <div className="terminal-output terminal-error">
          {entry.errorMessage}
        </div>
      ) : hasSegments ? (
        <div className="terminal-output">
          {entry.segments!.some((s) => s.plot) && (
            <button className="terminal-plot-toggle" onClick={onToggleCollapse}>
              {isCollapsed ? "▶ Show plots" : "▼ Hide plots"}
            </button>
          )}
          {entry.segments!.map((seg, i) =>
            seg.plot ? (
              !isCollapsed ? (
                <div className="terminal-plot" key={i}>
                  <PlotViewer data={seg.plot as any} />
                </div>
              ) : null
            ) : seg.text ? (
              <pre className="terminal-result" key={i}>
                {seg.text}
              </pre>
            ) : null,
          )}
        </div>
      ) : isManPage || isList ? (
        <pre className="terminal-output terminal-man-page">{entry.output}</pre>
      ) : isPlot ? (
        <div className="terminal-output terminal-plot">
          <button className="terminal-plot-toggle" onClick={onToggleCollapse}>
            {isCollapsed ? "▶ Show plot" : "▼ Hide plot"}
          </button>
          {!isCollapsed && <PlotViewer data={entry.result as any} />}
        </div>
      ) : isMatrix ? (
        <div className="terminal-output terminal-matrix">
          <pre>{entry.output}</pre>
          <button
            className="terminal-view-btn"
            onClick={() => onViewMatrix(entry)}
          >
            View Full
          </button>
        </div>
      ) : entry.latex && !entry.output?.includes("\n") ? (
        <div
          className="terminal-output terminal-latex"
          ref={(el) => {
            if (el && entry.latex) {
              try {
                // @ts-ignore - katex is loaded globally
                if (window.katex) {
                  window.katex.render(entry.latex, el, {
                    throwOnError: false,
                    displayMode: true,
                  });
                } else {
                  // KaTeX not loaded, show plain output
                  el.textContent = entry.output;
                }
              } catch {
                el.textContent = entry.output;
              }
            }
          }}
        />
      ) : entry.output ? (
        <pre className="terminal-output terminal-result">
          {String(entry.output)}
        </pre>
      ) : null}

      {/* Plots produced by a multi-statement block (rendered after any text). */}
      {hasPlots && (
        <div className="terminal-output terminal-plot">
          <button className="terminal-plot-toggle" onClick={onToggleCollapse}>
            {isCollapsed
              ? `▶ Show plot${entry.plots!.length > 1 ? "s" : ""}`
              : `▼ Hide plot${entry.plots!.length > 1 ? "s" : ""}`}
          </button>
          {!isCollapsed &&
            entry.plots!.map((pd, i) => (
              <PlotViewer key={i} data={pd as any} />
            ))}
        </div>
      )}
    </div>
  );
}

export function TerminalShell() {
  const {
    getInput,
    setInput,
    evaluate,
    clearInput,
    navigateInputHistory,
    getActiveSession,
    history,
    clearSessionHistory,
    resetEnvironment,
    environments,
    addToHistory,
    deleteHistoryEntry,
    collapsedPlots,
    togglePlotCollapsed,
  } = useCalculatorStore();

  const activeSession = getActiveSession();
  const activeSessionId = activeSession?.id ?? null;
  const input = getInput();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const [autocomplete, setAutocomplete] = useState<AutocompleteItem[]>([]);
  const [autocompleteIndex, setAutocompleteIndex] = useState(0);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [matrixViewEntry, setMatrixViewEntry] = useState<HistoryEntry | null>(
    null,
  );

  // Filter history for current session (oldest at top, newest at bottom for terminal-style)
  const sessionHistory = history
    .filter((h) => h.sessionId === activeSessionId)
    .slice()
    .reverse();

  // Focus input on mount and session change
  useEffect(() => {
    inputRef.current?.focus();
  }, [activeSessionId]);

  // Auto-resize textarea when input changes
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      const maxHeight = window.innerHeight * 0.5; // 50vh
      inputRef.current.style.height =
        Math.min(inputRef.current.scrollHeight, maxHeight) + "px";
    }
  }, [input]);

  // Scroll to bottom when history changes
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [sessionHistory.length]);

  // Get current word at cursor for autocomplete
  const getCurrentWord = useCallback((): {
    word: string;
    start: number;
    end: number;
  } => {
    const textarea = inputRef.current;
    if (!textarea) return { word: "", start: 0, end: 0 };

    const pos = textarea.selectionStart;
    const text = input;

    // Find word boundaries
    let start = pos;
    while (start > 0 && /[a-zA-Z0-9_]/.test(text[start - 1])) {
      start--;
    }
    let end = pos;
    while (end < text.length && /[a-zA-Z0-9_]/.test(text[end])) {
      end++;
    }

    return { word: text.slice(start, end), start, end };
  }, [input]);

  // Get environment variables for autocomplete
  const getEnvVariables = useCallback((): string[] => {
    if (!activeSessionId) return [];
    // Environment doesn't currently expose variable names
    // This would need enhancement to the calcscript Environment class
    if (!environments[activeSessionId]) return [];
    return [];
  }, [environments, activeSessionId]);

  // Build autocomplete suggestions
  const buildSuggestions = useCallback(
    (prefix: string): AutocompleteItem[] => {
      if (!prefix) return [];

      const suggestions: AutocompleteItem[] = [];

      // Built-in functions and constants
      const builtinSuggestions = getAutocompleteSuggestions(prefix);
      suggestions.push(...builtinSuggestions);

      // Environment variables (if we can access them)
      const envVars = getEnvVariables();
      for (const v of envVars) {
        if (v.toLowerCase().startsWith(prefix.toLowerCase())) {
          suggestions.push({ name: v, type: "variable" });
        }
      }

      return suggestions.slice(0, 15); // Limit to 15 suggestions
    },
    [getEnvVariables],
  );

  // Handle Tab completion
  const handleTab = useCallback(() => {
    const { word, start, end } = getCurrentWord();

    if (!showAutocomplete) {
      // First Tab press - show suggestions
      const suggestions = buildSuggestions(word);
      if (suggestions.length > 0) {
        setAutocomplete(suggestions);
        setAutocompleteIndex(0);
        setShowAutocomplete(true);

        // If only one suggestion, complete immediately
        if (suggestions.length === 1) {
          const completion = suggestions[0].name;
          const newText = input.slice(0, start) + completion + input.slice(end);
          setInput(newText);
          setShowAutocomplete(false);

          // Move cursor after completion
          setTimeout(() => {
            if (inputRef.current) {
              const newPos = start + completion.length;
              inputRef.current.setSelectionRange(newPos, newPos);
            }
          }, 0);
        }
      }
    } else {
      // Subsequent Tab - cycle through suggestions
      const nextIndex = (autocompleteIndex + 1) % autocomplete.length;
      setAutocompleteIndex(nextIndex);

      // Apply the suggestion
      const completion = autocomplete[nextIndex].name;
      const newText = input.slice(0, start) + completion + input.slice(end);
      setInput(newText);
    }
  }, [
    showAutocomplete,
    autocomplete,
    autocompleteIndex,
    getCurrentWord,
    buildSuggestions,
    input,
    setInput,
  ]);

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab completion
    if (e.key === "Tab") {
      e.preventDefault();
      handleTab();
      return;
    }

    // Escape closes autocomplete
    if (e.key === "Escape") {
      setShowAutocomplete(false);
      return;
    }

    // Close autocomplete on other keys
    if (showAutocomplete && !["ArrowUp", "ArrowDown"].includes(e.key)) {
      setShowAutocomplete(false);
    }

    // Enter to execute
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      const trimmedInput = input.trim();
      if (!trimmedInput) return;

      // Check for shell commands first
      const shellResult = processShellCommand(
        trimmedInput,
        () => activeSessionId && clearSessionHistory(activeSessionId),
        () => {
          if (!activeSessionId) return [];
          // Environment variable listing would need to expose internals from the environment
          // For now, return empty array
          return [];
        },
        () => history.filter((h) => h.sessionId === activeSessionId),
      );

      if (shellResult.handled) {
        if (shellResult.output) {
          // Add to history as a special entry
          addToHistory({
            input: trimmedInput,
            output: shellResult.output,
            result: shellResult.output,
            type: shellResult.isManPage
              ? ("man-page" as const)
              : shellResult.isList
                ? ("list" as const)
                : "string",
          });
        }
        clearInput();
      } else if (trimmedInput.toLowerCase() === "reset") {
        resetEnvironment();
        addToHistory({
          input: trimmedInput,
          output: "Environment reset.",
          result: "Environment reset.",
          type: "string",
        });
        clearInput();
      } else {
        // Regular evaluation
        evaluate();
      }
      return;
    }

    // Arrow navigation for history
    if (e.key === "ArrowUp" && inputRef.current) {
      const cursorPos = inputRef.current.selectionStart;
      const textBeforeCursor = input.slice(0, cursorPos);
      if (!textBeforeCursor.includes("\n")) {
        e.preventDefault();
        navigateInputHistory("up");
      }
    }

    if (e.key === "ArrowDown" && inputRef.current) {
      const cursorPos = inputRef.current.selectionStart;
      const textAfterCursor = input.slice(cursorPos);
      if (!textAfterCursor.includes("\n")) {
        e.preventDefault();
        navigateInputHistory("down");
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  return (
    <div className="terminal-shell">
      {/* Terminal output area */}
      <div className="terminal-output-area" ref={terminalRef}>
        {sessionHistory.length === 0 ? (
          <div className="terminal-welcome">
            <pre>
              {`  ██████╗ ██╗   ██╗ █████╗ ███╗   ██╗████████╗ █████╗ 
 ██╔═══██╗██║   ██║██╔══██╗████╗  ██║╚══██╔══╝██╔══██╗
 ██║   ██║██║   ██║███████║██╔██╗ ██║   ██║   ███████║
 ██║▄▄ ██║██║   ██║██╔══██║██║╚██╗██║   ██║   ██╔══██║
 ╚██████╔╝╚██████╔╝██║  ██║██║ ╚████║   ██║   ██║  ██║
  ╚══▀▀═╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═══╝   ╚═╝   ╚═╝  ╚═╝`}
            </pre>
            <p>Welcome to Quanta Calculator Shell</p>
            <p className="terminal-hint">
              Type <code>help</code> for usage info, or{" "}
              <code>man &lt;function&gt;</code> for documentation.
            </p>
            <p className="terminal-hint">Press Tab for autocomplete.</p>
          </div>
        ) : (
          sessionHistory.map((entry) => (
            <TerminalLine
              key={entry.id}
              entry={entry}
              onViewMatrix={setMatrixViewEntry}
              onDelete={() => deleteHistoryEntry(entry.id)}
              onEdit={() => setInput(entry.input)}
              isCollapsed={collapsedPlots.includes(entry.id)}
              onToggleCollapse={() => togglePlotCollapsed(entry.id)}
            />
          ))
        )}
      </div>

      {/* Input area with prompt */}
      <div className="terminal-input-area">
        <span className="terminal-prompt">&gt;&gt;</span>
        <div className="terminal-input-wrapper">
          <textarea
            ref={inputRef}
            className="terminal-input"
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter expression..."
            autoFocus
          />

          {/* Autocomplete dropdown */}
          {showAutocomplete && autocomplete.length > 0 && (
            <div className="terminal-autocomplete">
              {autocomplete.map((item, idx) => (
                <div
                  key={item.name}
                  className={`autocomplete-item ${idx === autocompleteIndex ? "selected" : ""}`}
                  onClick={() => {
                    const { start, end } = getCurrentWord();
                    const newText =
                      input.slice(0, start) + item.name + input.slice(end);
                    setInput(newText);
                    setShowAutocomplete(false);
                    inputRef.current?.focus();
                  }}
                >
                  <span className={`autocomplete-type type-${item.type}`}>
                    {item.type === "function"
                      ? "fn"
                      : item.type === "constant"
                        ? "const"
                        : item.type === "command"
                          ? "cmd"
                          : "var"}
                  </span>
                  <span className="autocomplete-name">{item.name}</span>
                  {item.synopsis && (
                    <span className="autocomplete-synopsis">
                      {item.synopsis}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Matrix viewer modal */}
      {matrixViewEntry && matrixViewEntry.result && (
        <MatrixViewer
          value={matrixViewEntry.result as CalcMatrix | CalcArray}
          name={matrixViewEntry.input}
          onClose={() => setMatrixViewEntry(null)}
        />
      )}
    </div>
  );
}
